import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { orgAccessCompanyIds } from '../utils/orgAccess';
import { isCompanyAdminRole } from '../utils/rolePermissions';

const CompanyContext = createContext(null);

export const useCompany = () => useContext(CompanyContext);

const ADMIN_ROLES = ['superadmin', 'admin'];

// The company to open for a user: the stored pick when the user can reach it
// (a direct company, or one an organization access grant reaches), else the
// first direct company, else the first org-granted company.
const pickCompanyId = (userData, storedCompanyId) => {
    const cc = userData.connectedCompanies;
    const connectedCompanies = Array.isArray(cc) ? cc : (cc?.companies || []);
    const orgCompanies = orgAccessCompanyIds(userData).all;
    const reachable = storedCompanyId
        && (connectedCompanies.includes(storedCompanyId) || orgCompanies.includes(storedCompanyId));
    return {
        targetCompanyId: reachable ? storedCompanyId : (connectedCompanies[0] || orgCompanies[0]),
        restored: Boolean(reachable),
    };
};

// Debug flag - set to true for verbose console logging
const DEBUG = false;

export const CompanyProvider = ({ children }) => {
    const { currentUser, userRole, isEmulating, isAuthenticated } = useAuth();
    const [companyData, setCompanyData] = useState(null);
    const [companyIdForAddress, setCompanyIdForAddress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCompanyData = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            // Wait for Firebase Auth to restore the session before ANY Firestore read.
            //
            // Under emulation currentUser resolves SYNCHRONOUSLY from localStorage
            // (see AuthContext's useState initializers), so on a hard refresh this
            // effect used to fire while the auth token was still restoring. The
            // `companies` read was then denied by rules, landed in the catch below,
            // and never retried — because during emulation currentUser keeps the same
            // identity when the real user arrives, so the effect never re-ran. Result:
            // companyIdForAddress stuck at null and every company-scoped page (e.g.
            // /billing) reported "No company found for your account".
            //
            // isAuthenticated tracks the REAL Firebase user, so this effect now re-runs
            // the moment the session restores. Same race the roleService init hit.
            if (!isAuthenticated) {
                setLoading(true);
                return;
            }

            // A needsSetup session (Google sign-in, no profile yet) has no
            // company, and the rules give it nothing: read nothing.
            if (currentUser.needsSetup) {
                setCompanyData(null);
                setCompanyIdForAddress(null);
                setError(null);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Handle role emulation - synthetic users don't exist in Firestore
                // Use the emulated user's companyId directly
                if (isEmulating && currentUser.isRoleEmulation) {
                    DEBUG && console.log('🎭 [CompanyContext] Role emulation detected, using emulated company data');
                    const emulatedCompanyId = currentUser.companyId || currentUser.companyID;

                    if (emulatedCompanyId) {
                        // Fetch the company data for the emulated company
                        const companiesQuery = query(
                            collection(db, 'companies'),
                            where('companyID', '==', emulatedCompanyId),
                            limit(1)
                        );

                        const companiesSnapshot = await getDocs(companiesQuery);

                        if (!companiesSnapshot.empty) {
                            const companyDoc = companiesSnapshot.docs[0];
                            const companyDocData = companyDoc.data();
                            const companyDocId = companyDoc.id;

                            const companyWithId = {
                                ...companyDocData,
                                id: companyDocId
                            };

                            DEBUG && console.log('🎭 [CompanyContext] Loaded emulated company:', companyDocData.name || emulatedCompanyId);
                            setCompanyData(companyWithId);
                            setCompanyIdForAddress(companyDocData.companyID);
                            setLoading(false);
                            return;
                        }
                    }

                    // No company found for emulation, set null
                    DEBUG && console.log('🎭 [CompanyContext] No company found for role emulation');
                    setCompanyData(null);
                    setCompanyIdForAddress(null);
                    setLoading(false);
                    return;
                }

                // CACHE REMOVED: Always fetch fresh company data from Firebase
                // This ensures connectedCarriers and other dynamic data is always up-to-date

                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

                if (!userDoc.exists()) {
                    throw new Error('User data not found.');
                }

                const userData = userDoc.data();

                // For super admins and multi-company users, check for stored company context first
                if (ADMIN_ROLES.includes(userRole) || isCompanyAdminRole(userRole)) {
                    // Check localStorage for a previously selected company
                    const storedCompanyId = localStorage.getItem('solushipx_selected_company_id');

                    // Use stored company if it exists and user has access to it, otherwise use first connected company
                    const { targetCompanyId, restored } = pickCompanyId(userData, storedCompanyId);

                    if (targetCompanyId) {
                        DEBUG && console.log(restored
                            ? `Admin/user restoring stored company context: ${targetCompanyId}`
                            : `Admin/user with no stored company context - using first connected company as default: ${targetCompanyId}`);

                        // Query for the target company document
                        const companiesQuery = query(
                            collection(db, 'companies'),
                            where('companyID', '==', targetCompanyId),
                            limit(1)
                        );

                        const companiesSnapshot = await getDocs(companiesQuery);

                        if (!companiesSnapshot.empty) {
                            const companyDoc = companiesSnapshot.docs[0];
                            const companyDocData = companyDoc.data();
                            const companyDocId = companyDoc.id;

                            const companyWithId = {
                                ...companyDocData,
                                id: companyDocId
                            };

                            setCompanyData(companyWithId);
                            setCompanyIdForAddress(companyDocData.companyID);
                            setLoading(false);
                            return;
                        }
                    }

                    // If no default company found, set no company context
                    DEBUG && console.log('Admin/user with no stored company context and no default company - setting no company context');
                    setCompanyData(null);
                    setCompanyIdForAddress(null);
                    setLoading(false);
                    return;
                }

                // For regular users, use the default company logic
                const companyIdValue = userData.connectedCompanies?.companies?.[0] || userData.companies?.[0]
                || orgAccessCompanyIds(userData).all[0];

                if (!companyIdValue) {
                    throw new Error('No company ID found.');
                }

                // Query for the company document where companyID field equals the value
                const companiesQuery = query(
                    collection(db, 'companies'),
                    where('companyID', '==', companyIdValue),
                    limit(1)
                );

                const companiesSnapshot = await getDocs(companiesQuery);

                if (companiesSnapshot.empty) {
                    throw new Error(`No company found with companyID: ${companyIdValue}`);
                }

                // Get the first matching document
                const companyDoc = companiesSnapshot.docs[0];
                const companyDocData = companyDoc.data();
                const companyDocId = companyDoc.id;

                // Save the Firebase document ID and the value for addressBook
                const companyWithId = {
                    ...companyDocData,
                    id: companyDocId
                };

                setCompanyData(companyWithId);
                setCompanyIdForAddress(companyDocData.companyID);
                setLoading(false);

            } catch (err) {
                console.error('Error fetching company data:', err);

                // For admin users and multi-company users, don't treat missing company as an error
                if (ADMIN_ROLES.includes(userRole) || isCompanyAdminRole(userRole)) {
                    DEBUG && console.log('Admin/user - treating missing company as normal');
                    setCompanyData(null);
                    setCompanyIdForAddress(null);
                    setError(null);
                } else {
                    setError(err.message || 'Failed to fetch company data.');
                }

                setLoading(false);
            }
        };

        fetchCompanyData();
    }, [currentUser, userRole, isEmulating, isAuthenticated]);

    // Function to clear company data on logout
    const clearCompanyData = () => {
        setCompanyData(null);
        setCompanyIdForAddress(null);

        // Clear stored company context from localStorage
        localStorage.removeItem('solushipx_selected_company_id');
        localStorage.removeItem('solushipx_selected_company_data');
        DEBUG && console.log('🧹 Cleared stored company context from localStorage');
    };

    // Function to clear stored company context (for returning to admin dashboard)
    const clearStoredCompanyContext = () => {
        localStorage.removeItem('solushipx_selected_company_id');
        localStorage.removeItem('solushipx_selected_company_data');
        DEBUG && console.log('🧹 Cleared stored company context');
    };

    // Function to refresh company data from Firestore
    const refreshCompanyData = async () => {
        if (!currentUser || !companyData?.id) {
            return;
        }

        try {
            // Fetch fresh data from Firestore using the document ID
            const companyDoc = await getDoc(doc(db, 'companies', companyData.id));

            if (!companyDoc.exists()) {
                throw new Error('Company document not found');
            }

            const freshCompanyData = {
                ...companyDoc.data(),
                id: companyDoc.id
            };

            // Update state
            setCompanyData(freshCompanyData);

        } catch (err) {
            console.error('Error refreshing company data:', err);
            setError(err.message || 'Failed to refresh company data');
        }
    };

    // Function to force refresh company data from Firestore (for credit hold updates)
    const forceRefreshCompanyData = async () => {
        if (!currentUser || currentUser.needsSetup) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Handle role emulation - synthetic users don't exist in Firestore
            if (isEmulating && currentUser.isRoleEmulation) {
                DEBUG && console.log('🎭 [CompanyContext] Force refresh for role emulation');
                const emulatedCompanyId = currentUser.companyId || currentUser.companyID;

                if (emulatedCompanyId) {
                    const companiesQuery = query(
                        collection(db, 'companies'),
                        where('companyID', '==', emulatedCompanyId),
                        limit(1)
                    );

                    const companiesSnapshot = await getDocs(companiesQuery);

                    if (!companiesSnapshot.empty) {
                        const companyDoc = companiesSnapshot.docs[0];
                        const companyDocData = companyDoc.data();
                        const companyDocId = companyDoc.id;

                        const companyWithId = {
                            ...companyDocData,
                            id: companyDocId
                        };

                        setCompanyData(companyWithId);
                        setCompanyIdForAddress(companyDocData.companyID);
                        setLoading(false);
                        return;
                    }
                }

                setCompanyData(null);
                setCompanyIdForAddress(null);
                setLoading(false);
                return;
            }

            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

            if (!userDoc.exists()) {
                throw new Error('User data not found.');
            }

            const userData = userDoc.data();

            // For super admins and multi-company users, check for stored company context first
            if (ADMIN_ROLES.includes(userRole) || isCompanyAdminRole(userRole)) {
                // Check localStorage for a previously selected company
                const storedCompanyId = localStorage.getItem('solushipx_selected_company_id');

                // Use stored company if it exists and user has access to it, otherwise use first connected company
                const { targetCompanyId, restored } = pickCompanyId(userData, storedCompanyId);

                if (targetCompanyId) {
                    DEBUG && console.log(restored
                        ? `Admin/user force refresh restoring stored company context: ${targetCompanyId}`
                        : `Admin/user force refresh with no stored company context - using first connected company as default: ${targetCompanyId}`);

                    // Query for the target company document
                    const companiesQuery = query(
                        collection(db, 'companies'),
                        where('companyID', '==', targetCompanyId),
                        limit(1)
                    );

                    const companiesSnapshot = await getDocs(companiesQuery);

                    if (!companiesSnapshot.empty) {
                        const companyDoc = companiesSnapshot.docs[0];
                        const companyDocData = companyDoc.data();
                        const companyDocId = companyDoc.id;

                        const companyWithId = {
                            ...companyDocData,
                            id: companyDocId
                        };

                        setCompanyData(companyWithId);
                        setCompanyIdForAddress(companyDocData.companyID);
                        setLoading(false);
                        return;
                    }
                }

                // If no default company found, set no company context
                DEBUG && console.log('Admin/user force refresh with no stored company context and no default company - setting no company context');
                setCompanyData(null);
                setCompanyIdForAddress(null);
                setLoading(false);
                return;
            }

            // For regular users, use the default company logic
            const companyIdValue = userData.connectedCompanies?.companies?.[0] || userData.companies?.[0]
                || orgAccessCompanyIds(userData).all[0];

            if (!companyIdValue) {
                throw new Error('No company ID found.');
            }

            // Query for the company document where companyID field equals the value
            const companiesQuery = query(
                collection(db, 'companies'),
                where('companyID', '==', companyIdValue),
                limit(1)
            );

            const companiesSnapshot = await getDocs(companiesQuery);

            if (companiesSnapshot.empty) {
                throw new Error(`No company found with companyID: ${companyIdValue}`);
            }

            const companyDoc = companiesSnapshot.docs[0];
            const companyDocData = companyDoc.data();
            const companyDocId = companyDoc.id;

            const companyWithId = {
                ...companyDocData,
                id: companyDocId
            };

            setCompanyData(companyWithId);
            setCompanyIdForAddress(companyDocData.companyID);
            setLoading(false);

            DEBUG && console.log('🔄 Company data force refreshed - fresh credit hold status loaded');

        } catch (err) {
            console.error('Error force refreshing company data:', err);
            setError(err.message || 'Failed to refresh company data');
            setLoading(false);
        }
    };

    // Function to manually set company data (for admin users switching context)
    const setCompanyContext = async (newCompanyData, returnPath = null) => {
        try {
            // Store the return path for "Return to Admin" functionality
            if (returnPath) {
                localStorage.setItem('solushipx_admin_return_path', returnPath);
            }

            // Handle null case (clearing company context)
            if (!newCompanyData) {
                setCompanyData(null);
                setCompanyIdForAddress(null);

                // Clear stored company context from localStorage
                if (ADMIN_ROLES.includes(userRole) || isCompanyAdminRole(userRole)) {
                    localStorage.removeItem('solushipx_selected_company_id');
                    localStorage.removeItem('solushipx_selected_company_data');
                    DEBUG && console.log('🧹 Cleared company context from localStorage');
                }

                return Promise.resolve();
            }

            // Update state
            setCompanyData(newCompanyData);
            setCompanyIdForAddress(newCompanyData.companyID);

            // Store the selected company ID in localStorage for persistence across page refreshes
            // We only store the ID, not the full data, to ensure fresh data is fetched from Firebase
            if (ADMIN_ROLES.includes(userRole) || isCompanyAdminRole(userRole)) {
                localStorage.setItem('solushipx_selected_company_id', newCompanyData.companyID);
                DEBUG && console.log('💾 Stored selected company ID to localStorage:', newCompanyData.companyID);
            }

            return Promise.resolve();
        } catch (err) {
            console.error('Error setting company context:', err);
            return Promise.reject(err);
        }
    };

    // Function to get the admin return path
    const getAdminReturnPath = () => {
        return localStorage.getItem('solushipx_admin_return_path') || '/admin/dashboard';
    };

    // Function to clear admin return path (when returning to admin)
    const clearAdminReturnPath = () => {
        localStorage.removeItem('solushipx_admin_return_path');
    };

    // Helper properties for company regional settings
    const defaultCurrency = companyData?.defaultCurrency || 'CAD';
    const defaultTimezone = companyData?.defaultTimezone || 'America/Toronto';

    const value = {
        companyData,
        companyIdForAddress,
        loading,
        error,
        clearCompanyData,
        refreshCompanyData,
        forceRefreshCompanyData,
        setCompanyContext,
        getAdminReturnPath,
        clearAdminReturnPath,
        clearStoredCompanyContext,
        isAdmin: ADMIN_ROLES.includes(userRole),
        // Regional settings helpers
        defaultCurrency,
        defaultTimezone
    };

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    );
}; 